import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Cell, Content, Flex, Text, View } from '@adobe/react-spectrum';
import { SORT_MAP } from '../../data/redux/slices/leaderboardSlice';
import './Commanders.css';
import {
    commandersFetchFiltered,
    fetchCommanderLeaderboardItems,
    setCommandersSortBy,
    setCommandersSortDirection,
    setSelectedListIndex,
} from '../../data/redux/slices/commandersSlice';
import { getGrades } from '../../data/ScoreDisplayUtils';
import { capBracketRating, capDisplayValueForSort, capPowerLevel } from '../../data/displayCaps';
import { Helmet } from 'react-helmet';
import { ResultsListing } from '../resultsListing/ResultsListing';
import '../resultsListing/renderers/ResultsCellRenderers.css';
import { setAppRoute } from '../../data/redux/slices/appSlice';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { NitroAd } from '../../ads/NitroAd';
import { fetchCommanderDetails } from '../../data/redux/slices/commanderDetailsSlice';
import { resetCommanderCardStats } from '../../data/redux/slices/commanderCardStatsSlice';
import HeroPanel from '../common/heroPanel/HeroPanel';
import { useFilterUrlSync } from '../../utils/useFilterUrlSync';
import { isDefaultFilterState } from '../../utils/filterUrl';

export function Commanders() {
    const dispatch = useDispatch();
    const listItems = useSelector((state) => state?.commanders?.listItems);
    const nextCursor = useSelector((state) => state?.commanders?.nextCursor);
    const isFetching = useSelector((state) => state?.commanders?.isFetching);
    const loadingState = useSelector((state) => state?.commanders?.loadingState);
    const searchFilters = useSelector((state) => state?.commanders?.filters);
    const details = useSelector((state) => state.commanders?.details);
    const sortBy = useSelector((state) => state?.commanders?.sortBy);
    const selectedListKey = useSelector((state) => state.commanders.selectedListKey);
    const selectedListIndex = useSelector((state) => state.commanders.selectedListIndex);

    const previewIsShowing = useSelector((state) => state.preview.isShowing);

    const handleSortChange = (sortIndex) => {
        dispatch(setCommandersSortBy(SORT_MAP[sortIndex], searchFilters));
        dispatch(fetchCommanderLeaderboardItems(null, true, SORT_MAP[sortIndex], sortDirection));
    };

    const sortDirection = useSelector((state) => state.commanders.sortDirection);
    const handleSortDirectionChange = (direction) => {
        dispatch(setCommandersSortDirection(direction));
        dispatch(fetchCommanderLeaderboardItems(null, true, sortBy, direction, searchFilters));
    };

    const handleFiltersChange = (filters) => {
        dispatch(commandersFetchFiltered(filters, sortBy));
    };

    useFilterUrlSync({
        context: 'commanders',
        filters: searchFilters,
        sortBy,
        sortDirection,
        onApply: ({ filters, sortBy: urlSortBy, sortDirection: urlSortDirection }) => {
            if (isDefaultFilterState(filters, urlSortBy, urlSortDirection, 'commanders')) return;
            dispatch(setCommandersSortBy(urlSortBy));
            dispatch(setCommandersSortDirection(urlSortDirection));
            dispatch(commandersFetchFiltered(filters, urlSortBy, urlSortDirection));
        },
    });

    const handleIndexSelection = (evn) => {
        dispatch(setSelectedListIndex(evn));
    };

    const getCellRenderer = (item, columnKey, options = {}) => {
        switch (columnKey) {
            case 'tileImages':
                item?.tileImages?.[0] || null;
                const colors = item?.colorIdentity?.match(/./g) || [];

                const getCellImage = () => {
                    if (item?.tileImages?.length > 1) {
                        return (
                            <div className="cellAvatar">
                                <Flex direction={'row'}>
                                    {item.tileImages.map((imageUri) => {
                                        return (
                                            <div
                                                style={{ maxWidth: '32px', overflow: 'hidden', alignContent: 'center' }}
                                            >
                                                <img style={{ marginLeft: -32 }} src={imageUri} height="50px" />
                                            </div>
                                        );
                                    })}
                                </Flex>
                            </div>
                        );
                    } else {
                        return (
                            <div className="cellAvatar">
                                <img src={item?.tileImages?.[0]} height="50px" />
                            </div>
                        );
                    }
                };

                return (
                    <div>
                        <Flex direction={'column'} height={70} margin={5}>
                            {getCellImage()}
                            <div className="cellAvatarManaSymbols">
                                {colors && colors.length > 0 && (
                                    <div>
                                        {colors?.map((symbol) => {
                                            return (
                                                <span
                                                    key={`${item?.id}__color${symbol?.toLowerCase()}`}
                                                    className={`mana xs s${symbol?.toLowerCase()}`}
                                                    style={{ opacity: '1' }}
                                                ></span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </Flex>
                    </div>
                );
                break;
            case 'score':
                const isCountSort = sortBy?.field === 'count';
                const grade = getGrades(item);
                const prop = sortBy?.enum.toLowerCase();

                if (isCountSort) {
                    const count = Number(item?.count) || 0;
                    return (
                        <span>
                            <Flex direction={'column'} justifyContent={'space-around'} alignItems={'center'}>
                                <span data-role={'large'} style={{ color: grade?.[prop]?.color }}>
                                    {count}
                                </span>
                                {!details && (
                                    <span data-role={'small'} style={{ marginTop: 5 }}>
                                        DECK{count === 1 ? '' : 'S'}
                                    </span>
                                )}
                            </Flex>
                        </span>
                    );
                }

                const score = capDisplayValueForSort(item?.displayValue || 0, sortBy?.enum);
                let floatValue = parseFloat(/[0-9]*\.[0-9]/.exec(score.toString())?.[0] || `${score}`);
                if (!floatValue.toString().includes(`.`)) {
                    floatValue = `${floatValue}.0`;
                }

                return (
                    <span>
                        <Flex direction={'column'} justifyContent={'space-around'} alignItems={'center'}>
                            <span data-role={'large'} style={{ color: grade?.[prop]?.color }}>
                                {floatValue}
                            </span>
                            {!details && (
                                <span data-role={'small'} style={{ marginTop: 5 }}>
                                    {item?.count} DECK{item?.count > 1 ? 'S' : ''}
                                </span>
                            )}
                        </Flex>
                    </span>
                );
                break;
            case 'commanders':
                let commanderList = item?.commanders;
                if (!Array.isArray(commanderList)) {
                    commanderList = [item?.commanders];
                }

                commanderList = commanderList.map((item) => {
                    if (item.hasOwnProperty('name')) {
                        return item.name;
                    }

                    return item;
                });

                // Partner commanders join with " + " — keeps "Tevesh Szat,
                // Doom of Fools + Thrasios, Triton Hero" legible when one
                // commander has a comma in its own name.
                const commanders = commanderList.filter(Boolean).join(' + ');

                return (
                    <div>
                        <Flex direction={'column'} height={80} justifyContent={'center'}>
                            <Text data-role={'small-header'}>{commanders}</Text>
                        </Flex>
                    </div>
                );

                break;
            case 'row_footer': {
                const formatMetric = (value, decimals = 1) => {
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0) return null;
                    return n.toFixed(decimals);
                };
                const formatSaltMetric = (value) => {
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0) return null;
                    return n >= 100 ? Math.round(n).toString() : n.toFixed(1);
                };
                const isNarrow =
                    Boolean(options?.compact) ||
                    (typeof window !== 'undefined' && window.innerWidth && window.innerWidth < 700);
                const chips = [];
                if (sortBy?.enum !== SORT_MAP.POWERLEVEL.enum) {
                    const v = formatMetric(capPowerLevel(item?.powerLevelRating), 1);
                    if (v) chips.push({ key: 'power', label: 'PWR', value: v });
                }
                if (sortBy?.enum !== SORT_MAP.BRACKET.enum) {
                    const v = formatMetric(capBracketRating(item?.bracketRating), 1);
                    if (v) chips.push({ key: 'bracket', label: isNarrow ? 'BRKT' : 'BRACKET', value: v });
                }
                if (sortBy?.enum !== SORT_MAP.SALT.enum) {
                    const v = formatSaltMetric(item?.saltRating);
                    if (v) chips.push({ key: 'salt', label: 'SALT', value: v });
                }
                if (chips.length === 0) return null;
                return (
                    <div className="resultsRowMetricStrip">
                        {chips.map((chip) => (
                            <span key={chip.key} className={`resultsRowMetric resultsRowMetric--${chip.key}`}>
                                <span className="resultsRowMetricLabel">{chip.label}</span>
                                <span className="resultsRowMetricValue">{chip.value}</span>
                            </span>
                        ))}
                    </div>
                );
            }
            default:
                if (columnKey === 'row_footer') return null;
                return (
                    <div>
                        <div className="TextCell">{item[columnKey]}</div>
                    </div>
                );
        }
    };

    const getColumnRenderer = (item) => {
        let content;

        if (item.uid === 'score') {
            content = <div />;
        } else if (item.uid === 'tileImages') {
            content = <div />;
        } else {
            content = item.name;
        }

        return <div style={{ minWidth: '83px' }}>{content}</div>;
    };

    const handleLeaderboardSelectionChange = (id) => {
        try {
            if (id) {
                dispatch(fetchCommanderDetails(id));
                dispatch(resetCommanderCardStats({ id: null }));
                dispatch(setAppRoute({ route: `/commanders/id/${id}` }));
            }
        } catch (error) {}
    };

    const handleLoadMore = () => {
        if (!isFetching && nextCursor !== null) {
            dispatch(fetchCommanderLeaderboardItems(nextCursor, false, sortBy, sortDirection));
        }
    };

    const max = window.innerWidth - 180;
    let columns = [
        { name: 'tileImages', uid: 'tileImages', maxWidth: 65, minWidth: 65, width: 65 },
        { name: 'Commander(s)', uid: 'commanders', maxWidth: max, minWidth: 200, width: max },
        { name: '', uid: 'score', width: 120 },
    ];

    const filteredListItems = (listItems || []).filter((item) => {
        return item !== null && item.hasOwnProperty('id');
    });

    const helmetName = `Commanders`;
    const helmetCanonical = `https://www.commandersalt.com/commanders`;
    const helmetDescription = `Who is the saltiest Commander of them all?`;
    const helmetTitle = `Commandersalt.com - Who is the saltiest Commander of them all?`;

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />
            <div className="heroPanelWrap browseHeroWrap">
                <HeroPanel
                    tone="red"
                    eyebrow="Browse · Commanders"
                    title="The saltiest commanders in the format"
                    hint="Sort by power, bracket, salt, or popularity. Tap a commander to see how its decks distribute across power levels and which cards show up most often."
                />
            </div>
            <NitroAd id="commanders" />
            <ResultsListing
                includeSortByCount={true}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSortChange={handleSortChange}
                handleSortDirectionChange={handleSortDirectionChange}
                onFiltersChange={handleFiltersChange}
                filters={searchFilters}
                restrictToCommander={true}
                disableNavChanges={true}
                allowDeckPreview={false}
                handleSelectionChange={handleLeaderboardSelectionChange}
                hasMore={nextCursor !== null}
                handleIndexSelection={handleIndexSelection}
                selectedListIndex={selectedListIndex}
                selectedListIndexPrefix={'commanders_'}
                selectedListKey={selectedListKey}
                columns={columns}
                showFilterPanel={true}
                filterContext="commanders"
                columnRenderer={getColumnRenderer}
                listItems={filteredListItems}
                cellRenderer={getCellRenderer}
                previewIsShowing={previewIsShowing}
                handleLoadMore={handleLoadMore}
                loadingState={loadingState}
            />
        </Flex>
    );
}
